import { Lock, Share2, Users } from 'lucide-react';
import { useCallback } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { cn } from '@/_shared/lib/utils';

// Public chat sharing is intentionally absent here (AI-628 alpha disables it;
// rebuild tracked in AI-632). Workspace-visibility sharing is unaffected —
// chats can still be shared with teammates inside the same workspace.
type Visibility = 'private' | 'workspace';

interface Props {
  visibility: Visibility;
  onUpdateVisibility: (visibility: Visibility) => void;
  pending?: boolean;
}

const OPTIONS: { value: Visibility; label: string; icon: typeof Lock; description: string }[] = [
  { value: 'private', label: 'Only me', icon: Lock, description: 'Only you can view this chat' },
  { value: 'workspace', label: 'Workspace members', icon: Users, description: 'Anyone in your workspace can view' },
];

export function ChatSharePopover({ visibility, onUpdateVisibility, pending = false }: Props): React.ReactElement {
  const handleSelect = useCallback(
    (next: Visibility) => {
      if (next === visibility) return;
      onUpdateVisibility(next);
    },
    [visibility, onUpdateVisibility],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="share-trigger">
          <Share2 className="size-3.5" />
          Share
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">Share this chat</p>
          <div className="space-y-1" role="radiogroup" aria-label="Chat visibility">
            {OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={visibility === opt.value}
                disabled={pending}
                onClick={() => handleSelect(opt.value)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                  visibility === opt.value
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  pending && 'pointer-events-none opacity-50',
                )}
                data-testid={`visibility-${opt.value}`}
              >
                <opt.icon className="size-4 shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs opacity-70">{opt.description}</div>
                </div>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Anonymous public links are temporarily disabled while we ship the new sharing model.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
